import { useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useArticleBySlug, useArticlesByCategory } from "@/hooks/useArticles";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ArticleHeader from "@/components/article/ArticleHeader";
import ArticleContent from "@/components/article/ArticleContent";
import ArticleShareButtons from "@/components/article/ArticleShareButtons";
import MySabbatAppCTA from "@/components/article/MySabbatAppCTA";
import RelatedArticles from "@/components/article/RelatedArticles";
import ReadingProgressBar from "@/components/article/ReadingProgressBar";
import ScrollIndicator from "@/components/article/ScrollIndicator";
import ScrollToTop from "@/components/ScrollToTop";
import { ArrowLeft } from "lucide-react";

// Parse FAQ items from article content for structured data
function parseFaqItemsFromContent(content: string): { question: string; answer: string }[] {
  const faqItems: { question: string; answer: string }[] = [];
  
  // Match [faq] ... [/faq] blocks
  const faqBlockRegex = /\[faq\]([\s\S]*?)\[\/faq\]/g;
  let match;
  
  while ((match = faqBlockRegex.exec(content)) !== null) {
    const faqContent = match[1];
    const lines = faqContent.split("\n");
    
    let currentQuestion = "";
    let currentAnswer: string[] = [];
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith("Q:")) {
        // Save previous Q&A if exists
        if (currentQuestion && currentAnswer.length > 0) {
          faqItems.push({
            question: currentQuestion,
            answer: currentAnswer.join(" ").trim()
          });
        }
        currentQuestion = trimmed.slice(2).trim();
        currentAnswer = [];
      } else if (trimmed.startsWith("A:")) {
        currentAnswer.push(trimmed.slice(2).trim());
      } else if (currentQuestion && trimmed) {
        currentAnswer.push(trimmed);
      }
    }
    
    // Don't forget the last Q&A
    if (currentQuestion && currentAnswer.length > 0) {
      faqItems.push({
        question: currentQuestion,
        answer: currentAnswer.join(" ").trim()
      });
    }
  }
  
  return faqItems;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useArticleBySlug(slug || "");
  const { data: relatedArticles } = useArticlesByCategory(
    article?.category?.slug || ""
  );

  // Scroll to top and track view when article changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // Track article view separately
  useEffect(() => {
    const trackView = async () => {
      if (article?.id) {
        try {
          await supabase.rpc('increment_article_view', { article_id: article.id });
        } catch (error) {
          console.error('Failed to track view:', error);
        }
      }
    };
    trackView();
  }, [article?.id]);

  // Filter out current article from related
  const otherArticles = relatedArticles?.filter((a) => a.id !== article?.id).slice(0, 3);

  // Parse FAQ items from article content for SEO structured data
  const faqItems = useMemo(() => {
    if (!article?.content) return [];
    return parseFaqItemsFromContent(article.content);
  }, [article?.content]);

  // Calculate word count for SEO
  const wordCount = useMemo(() => {
    if (!article?.content) return undefined;
    return article.content.split(/\s+/).length;
  }, [article?.content]);

  // Build breadcrumbs for SEO
  const breadcrumbs = useMemo(() => {
    const items = [{ name: "Forside", url: "https://mysabbat.dk" }];
    if (article?.category) {
      items.push({
        name: article.category.name,
        url: `https://mysabbat.dk/kategori/${article.category.slug}`
      });
    }
    if (article) {
      items.push({
        name: article.title,
        url: `https://mysabbat.dk/artikel/${article.slug}`
      });
    }
    return items;
  }, [article]);

  if (error) {
    return (
      <>
        <SEOHead 
          title="Artikel ikke fundet | MySabbat" 
          description="Denne artikel eksisterer ikke eller er blevet fjernet."
          path={`/artikel/${slug}`}
        />
        <div className="min-h-screen bg-background flex flex-col">
          <Header />
          <main className="flex-1 container mx-auto px-4 py-16 text-center">
            <h1 className="text-2xl font-bold mb-4">Artikel ikke fundet</h1>
            <p className="text-muted-foreground mb-8">
              Denne artikel eksisterer ikke eller er blevet fjernet.
            </p>
            <Link to="/" className="text-primary hover:underline">
              Gå til forsiden
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={article ? `${article.title} | MySabbat` : "Artikel | MySabbat"}
        description={article?.excerpt || "Læs mere om sabbatår på MySabbat.dk"}
        path={`/artikel/${slug}`}
        type="article"
        publishedTime={article?.created_at}
        modifiedTime={article?.updated_at}
        keywords={article?.category ? [article.category.name, "sabbatår", article.title.split(" ").slice(0, 3).join(" ")] : undefined}
        faqItems={faqItems}
        breadcrumbs={breadcrumbs}
        wordCount={wordCount}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <ReadingProgressBar />
        <Header />
        <ScrollToTop />
        {wordCount && <ScrollIndicator wordCount={wordCount} />}

        <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
          {/* Loading state */}
          {isLoading ? (
            <div className="space-y-6 animate-pulse">
              <div className="h-4 w-32 bg-muted rounded" />
              <div className="flex gap-3">
                <div className="h-6 w-24 bg-muted rounded-full" />
                <div className="h-6 w-32 bg-muted rounded" />
              </div>
              <div className="h-12 w-3/4 bg-muted rounded" />
              <div className="h-6 w-full bg-muted rounded" />
              <div className="space-y-3 mt-8">
                <div className="h-4 w-full bg-muted rounded" />
                <div className="h-4 w-5/6 bg-muted rounded" />
                <div className="h-4 w-4/5 bg-muted rounded" />
              </div>
            </div>
          ) : article ? (
            <>
              {/* Article Header with breadcrumb, category, reading time */}
              <ArticleHeader article={article} />

              {/* Article content with enhanced markdown */}
              <article className="prose prose-lg max-w-none">
                <ArticleContent content={article.content} />
              </article>

              {/* MySabbat App promotion */}
              <MySabbatAppCTA />

              {/* Share buttons */}
              <ArticleShareButtons title={article.title} slug={article.slug} />

              {/* Related articles */}
              {otherArticles && otherArticles.length > 0 && article.category && (
                <RelatedArticles
                  articles={otherArticles}
                  categoryName={article.category.name}
                  categorySlug={article.category.slug}
                />
              )}

              {/* Back to home link */}
              <div className="mt-12 pt-8 border-t border-border">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  Tilbage til forsiden
                </Link>
              </div>
            </>
          ) : null}
        </main>

        <Footer />
      </div>
    </>
  );
}
